/**
 * Step 2 — From-an-Ad input.
 *
 * Side-by-side layout:
 *   left  — ad creative preview (headline, body, thumbnail) once an ad ID
 *           is supplied
 *   right — the per-section confidence-tagged draft, which gets piped into
 *           the parent's DraftPreviewPanel via `onLoaded`
 *
 * On commit the parent overlay turns the BriefDraft into a CreateBriefRequest.
 */

import { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Search, Zap } from 'lucide-react';
import {
  generationApi,
  type AdPreview,
  type BriefDraft,
} from '@/services/api';

interface Props {
  clientUid: string;
  personaId?: string;
  defaultAdId?: string;
  onLoaded: (draft: BriefDraft, preview: AdPreview) => void;
}

export function FromAnAdInput({
  clientUid,
  personaId,
  defaultAdId,
  onLoaded,
}: Props) {
  const [adId, setAdId] = useState(defaultAdId ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AdPreview | null>(null);

  const fetchDraft = async (id: string) => {
    if (!id || !clientUid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await generationApi.briefFromAd({
        ad_id: id,
        client_uid: clientUid,
        persona_id: personaId,
      });
      setPreview(res.ad_preview);
      onLoaded(res.draft, res.ad_preview);
    } catch (err) {
      setError((err as Error).message || 'Failed to extract brief from ad');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (defaultAdId) {
      void fetchDraft(defaultAdId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAdId]);

  return (
    <div className="space-y-4">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Ad ID
        </label>
        <div className="mt-1 flex items-center gap-2">
          <input
            type="text"
            value={adId}
            onChange={(e) => setAdId(e.target.value)}
            placeholder="act_1234567890_ad_987654321"
            disabled={loading}
            className="flex-1 text-sm text-slate-800 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300/40 focus:border-sky-400/60 font-mono"
          />
          <button
            type="button"
            onClick={() => fetchDraft(adId.trim())}
            disabled={loading || !adId.trim() || !clientUid}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Extract
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          Paste the ad ID from your catalog or the asset detail drawer.
        </p>
      </div>

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
          {error}
        </div>
      )}

      {preview && <AdAssetPreview preview={preview} />}

      {!preview && !loading && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
          <Zap size={18} className="text-slate-300 mx-auto mb-2" />
          <p className="text-[12px] text-slate-500">
            Enter an ad ID and click Extract — the brief draft will populate the
            preview pane on the right.
          </p>
        </div>
      )}
    </div>
  );
}

function AdAssetPreview({ preview }: { preview: AdPreview }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-stretch gap-0">
        {preview.thumbnail_url || preview.image_url ? (
          <div className="w-32 shrink-0 bg-slate-100">
            <img
              src={preview.thumbnail_url || preview.image_url || ''}
              alt={preview.headline}
              className="w-full h-full object-cover"
            />
          </div>
        ) : null}
        <div className="flex-1 min-w-0 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {preview.platform} · {preview.ad_id.slice(0, 12)}…
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            {preview.headline || '(no headline)'}
          </div>
          {preview.body && (
            <p className="text-[12px] text-slate-600 mt-1 leading-snug line-clamp-3">
              {preview.body}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
            {preview.cta && (
              <span className="font-mono uppercase">{preview.cta}</span>
            )}
            {preview.destination_url && (
              <a
                href={preview.destination_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sky-600 hover:underline"
              >
                <ExternalLink size={10} />
                Landing
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
